"use client";

import { motion } from "framer-motion";
import {
  Bell,
  BookOpen,
  ChevronRight,
  Sparkles,
  Target,
  Trophy,
  Wallet,
} from "lucide-react";

export default function DashboardPreview() {
  return (
    <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#0B1730] p-4 shadow-2xl sm:p-6">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-gold">
            Illustrative preview
          </p>
          <h2 className="mt-1 text-2xl font-black tracking-tight text-white sm:text-3xl">
            Your learning hub
          </h2>
          <p className="mt-1 text-sm text-white/50">
            A glimpse of the TCD student experience
          </p>
        </div>

        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-gold/30 bg-brand-gold/10 font-bold text-brand-gold sm:h-11 sm:w-11">
          TCD
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-xs leading-5 text-white/55">
        <Sparkles className="h-4 w-4 shrink-0 text-brand-gold" />
        Sample interface — figures and activity are not live account data.
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <MetricCard
          icon={<BookOpen className="h-5 w-5" />}
          title="Practice"
          value="Build skills"
        />
        <MetricCard
          icon={<Target className="h-5 w-5" />}
          title="Exams"
          value="Test yourself"
        />
        <MetricCard
          icon={<Wallet className="h-5 w-5" />}
          title="Wallet"
          value="Credits & activity"
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <motion.div
          whileHover={{ y: -3 }}
          className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 sm:p-5 lg:col-span-2"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-gold">
                Practice experience
              </p>
              <h3 className="mt-2 text-xl font-bold tracking-tight text-white sm:text-2xl">
                Prepare for your next challenge
              </h3>
              <p className="mt-2 max-w-lg text-sm leading-6 text-white/55">
                Practice questions, attempt online exams and review your
                performance as you work towards your goals.
              </p>
            </div>
            <BookOpen className="h-7 w-7 shrink-0 text-brand-gold sm:h-8 sm:w-8" />
          </div>

          <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-brand-gold">
            Explore the exam experience
            <ChevronRight className="h-4 w-4" />
          </div>
        </motion.div>

        <motion.div
          whileHover={{ y: -3 }}
          className="rounded-2xl border border-brand-gold/20 bg-gradient-to-br from-brand-gold/15 via-white/[0.04] to-white/[0.02] p-4 text-white sm:p-5"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-brand-gold/20 bg-brand-gold/10">
            <Trophy className="h-5 w-5 text-brand-gold" />
          </div>
          <p className="mt-4 text-sm text-white/60">Your journey</p>
          <h3 className="mt-1 text-xl font-black">Every step counts</h3>
          <p className="mt-2 text-sm leading-6 text-white/55">
            Work towards milestones and learn from each attempt.
          </p>
        </motion.div>
      </div>

      <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 sm:p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-gold/20 bg-brand-gold/10">
            <Target className="h-5 w-5 text-brand-gold" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/45">
              Progress overview
            </p>
            <h3 className="mt-1 text-base font-bold text-white sm:text-lg">
              Build consistency, one attempt at a time
            </h3>
          </div>
          <Bell className="hidden h-5 w-5 shrink-0 text-white/35 sm:block" />
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10">
          <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-brand to-brand-gold" />
        </div>
        <p className="mt-2 text-xs text-white/40">
          Decorative progress indicator for this preview only
        </p>
      </div>
    </div>
  );
}

function MetricCard({
  icon,
  title,
  value,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 transition-colors hover:border-brand-gold/20 hover:bg-white/[0.06] sm:p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-gold/20 bg-brand-gold/10 text-brand-gold">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-white/45">
            {title}
          </p>
          <p className="mt-1 text-sm font-bold leading-5 text-white sm:text-base">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}
